// import components
import { Stack, Tooltip, Typography } from '@mui/material';
import { Icon } from '@sdb/planning-ui';
import { RosterDataGrid } from 'components/shared/RosterViewDataGrid';
import PrescriptionRuleCell from './PrescriptionRuleCell';

// import external libraries
import { memo, useCallback, useMemo } from 'react';

// import hooks
import { useRosterIntervalDays } from 'components/shared/RosterViewDataGrid';
import { groupRosterDaysByWeek } from 'components/shared/RosterViewDataGrid/hooks/useRosterTimelineColumns/utils';

// import types
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import type { PrescriptionRuleResponse } from 'generated/index';
import type { PrescriptionRuleCellValue } from './PrescriptionRuleCell';
import type {
  PrescriptionRuleRow,
  PrescriptionRulesGridProps
} from './PrescriptionRulesGrid.types';

// import utils & constants
import {
  ROW_HEIGHT,
  MAX_WEEKS,
  buildWeekDataForRule
} from './PrescriptionRulesGrid.utils';

// import styles
import { dataGridSx } from './PrescriptionRulesGrid.styles';

const PrescriptionRulesGrid = ({
  prescription,
  timelineView,
  dateInterval
}: PrescriptionRulesGridProps) => {
  const interval = useMemo(() => {
    if (dateInterval) return dateInterval;

    if (timelineView === 'weeksOnlyRelative') {
      const startDate = prescription.prescriptionRules.reduce(
        (earliest, rule) => {
          const ruleStart = new Date(rule.start);
          return ruleStart < earliest ? ruleStart : earliest;
        },
        new Date()
      );

      const endDate = prescription.prescriptionRules.reduce((latest, rule) => {
        if (!rule.end) return latest;
        const ruleEnd = new Date(rule.end);
        return ruleEnd > latest ? ruleEnd : latest;
      }, new Date(0));

      const fallbackEnd = prescription.endDate
        ? new Date(prescription.endDate)
        : undefined;
      const maxEnd = new Date(startDate);
      maxEnd.setDate(maxEnd.getDate() + MAX_WEEKS * 7 - 1);

      const effectiveEnd =
        endDate.getTime() > 0 ? endDate : fallbackEnd ?? maxEnd;

      return {
        startDate,
        endDate: effectiveEnd
      };
    }

    const startDate = new Date(prescription.startDate);
    const maxEnd = new Date(startDate);
    maxEnd.setDate(maxEnd.getDate() + MAX_WEEKS * 7 - 1);
    const endDate = new Date(prescription.endDate);

    return {
      startDate,
      endDate: endDate < maxEnd ? endDate : maxEnd
    };
  }, [
    dateInterval,
    timelineView,
    prescription.startDate,
    prescription.endDate,
    prescription.prescriptionRules
  ]);

  const { rosterDays: allRosterDays } = useRosterIntervalDays({
    defaultInterval: interval
  });

  const { rosterDays, weeks } = useMemo(() => {
    const allWeeks = groupRosterDaysByWeek(allRosterDays);
    const cappedWeeks = allWeeks.slice(0, MAX_WEEKS);
    return { rosterDays: cappedWeeks.flat(), weeks: cappedWeeks };
  }, [allRosterDays]);

  const rows: PrescriptionRuleRow[] = useMemo(
    () =>
      prescription.prescriptionRules.map((rule: PrescriptionRuleResponse) => ({
        id: rule.id,
        description: rule.description,
        activityName: rule.activity.name,
        activityCode: rule.activity.code,
        ...buildWeekDataForRule(rule, weeks, prescription.endDate)
      })),
    [prescription.prescriptionRules, weeks]
  );

  const renderTimelineCell = useCallback(
    (params: GridRenderCellParams) => (
      <PrescriptionRuleCell value={params.value as PrescriptionRuleCellValue} />
    ),
    []
  );

  const fixedColumnsLeft: GridColDef<PrescriptionRuleRow>[] = useMemo(
    () => [
      {
        field: 'activity',
        headerName: '',
        width: 250,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            height="100%"
            width="100%"
          >
            <Typography variant="body1" noWrap>
              {params.row.activityName}
            </Typography>
            {params.row.description && (
              <Tooltip title={params.row.description} arrow>
                <Icon type="note" sx={{ width: 18 }} color="action" />
              </Tooltip>
            )}
          </Stack>
        )
      }
    ],
    []
  );

  return (
    <RosterDataGrid<PrescriptionRuleRow>
      rosterDays={rosterDays}
      rows={rows}
      fixedColumnsLeft={fixedColumnsLeft}
      timelineView="weeksOnly"
      pinnedColumns={{ left: ['activity'] }}
      renderTimelineCell={renderTimelineCell}
      rowHeight={ROW_HEIGHT}
      timelineColumnWidth={100}
      sx={dataGridSx}
    />
  );
};

export default memo(PrescriptionRulesGrid);
